import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { Popup } from '@components/atoms/Popup';
import { useData } from '@hooks/useData';
import { useNotifications } from '@hooks/useNotifications';
import { useReserveProduct } from '@services/useReserveProduct';
import { ProductQuantityCard } from '@components/molecules/ProductQuantityCard/ProductQuantityCard';
import { useCalculateProductPrice } from '@services/useCalculateProductPrice';
import { Input, InputStates, InputTypes, InputVariants } from '@components/atoms/Input';
import { Space } from '@components/atoms/Space';
import { formatPhoneNumber } from '@utils/format';
import { InputErrors, ProductAvailability } from '@types';
import styles from './ReserveProductModal.module.css';

interface ReserveProductFormErrors {
    general: InputErrors;
    description: InputErrors;
}

interface ReserveProductModalProps {
    product: {
        uuid: string;
        name: string;
        image: string;
        quantity: number;
        availability?: ProductAvailability;
    };
    attributesValues?: Record<string, { uuid: string; value: string }>;
    merchant: string;
    show: boolean;
    onClose: () => void;
}

export const ReserveProductModal: FC<ReserveProductModalProps> = ({ product, attributesValues, merchant, show, onClose }) => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestReserveProduct,
        loading: loadingReserveProduct,
        success: successReserveProduct,
        errorMessage: errorReserveProduct,
        validationErrors: validationErrorsReserveProduct,
        lastResponseTime: lastReserveProduct,
    } = useReserveProduct();
    const {
        request: requestCalculateProductPrice,
        data: dataCalculateProductPrice,
        loading: loadingCalculateProductPrice,
        errorMessage: errorCalculateProductPrice,
        lastResponseTime: lastCalculateProductPrice,
    } = useCalculateProductPrice();
    const { user } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('reserve-product-modal-component.title'),
        merchantLabel: t('reserve-product-modal-component.merchant.label'),
        descriptionLabel: t('reserve-product-modal-component.description.label'),
        descriptionPlaceholder: t('reserve-product-modal-component.description.placeholder'),
        yourDataLabel: t('reserve-product-modal-component.your-data.label'),
        yourDataNameLabel: t('reserve-product-modal-component.your-data.name.label'),
        yourDataEmailLabel: t('reserve-product-modal-component.your-data.email.label'),
        yourDataPhoneLabel: t('reserve-product-modal-component.your-data.phone.label'),
        waitForStoreFeedbackLabel: t('reserve-product-modal-component.your-data.wait-for-store-feedback.label'),
        reserveButton: t('reserve-product-modal-component.reserve.button'),
        cancelButton: t('reserve-product-modal-component.cancel.button'),
    };

    const [description, setDescription] = useState<string>('');

    const [errors, setErrors] = useState<ReserveProductFormErrors>({
        general: [],
        description: [],
    });

    const [quantity, setQuantity] = useState<number | null>(null);

    const attributesValuesNames = Object.fromEntries(
        Object.entries(attributesValues || []).map(([key, value]) => [key, value.value])
    );

    const attributesValuesIds = Object.fromEntries(
        Object.entries(attributesValues || []).map(([key, value]) => [key, value.uuid])
    );

    useEffect(() => {
        if (show) {
            setQuantity(product.quantity);
        } else {
            setQuantity(null);
        }
    }, [show]);

    useEffect(() => {
        if (quantity) {
            requestCalculateProductPrice(
                { uuid: product.uuid },
                {
                    quantity: quantity,
                    attributes_values: attributesValuesIds,
                }
            );
        }
    }, [quantity]);

    useEffect(() => {
        if (lastReserveProduct) {
            // show error when it's a new endpoint call with error
            if (errorReserveProduct || validationErrorsReserveProduct) {
                setErrors({
                    general: [
                        errorReserveProduct as string,
                        // all validation error messages except 'description'
                        ...Object.entries(validationErrorsReserveProduct || {})
                            .filter(([key]) => key !== 'description')
                            .flatMap(([, value]) => value),
                    ],
                    // match form request errors with frontend errors object
                    description: validationErrorsReserveProduct?.description || [],
                });
            } else {
                showDefaultNotification({ type: 'success', message: successReserveProduct || '' });
                onClose();
                resetForm();
            }
        }
    }, [lastReserveProduct]);

    const resetErrors = () => {
        setErrors({
            general: [],
            description: [],
        });
    };

    useEffect(() => {
        if (loadingReserveProduct) {
            resetErrors();
        }
    }, [loadingReserveProduct]);

    useEffect(() => {
        resetErrors();
    }, [description]);

    const resetForm = () => {
        resetErrors();
        setDescription('');
    };

    useEffect(() => {
        if (loadingReserveProduct) {
            setErrors({
                general: [],
                description: [],
            });
        }
    }, [loadingReserveProduct]);

    useEffect(() => {
        if (lastCalculateProductPrice && errorCalculateProductPrice) {
            showDefaultNotification({ type: 'error', message: errorCalculateProductPrice || '' });
            onClose();
            resetForm();
        }
    }, [lastCalculateProductPrice]);

    const handleOnClickReserve = () => {
        quantity &&
            requestReserveProduct({
                uuid: product.uuid,
                quantity: quantity,
                attributes_values: attributesValuesIds,
                description: description,
            });
    };

    return (
        <Popup
            show={show}
            onHide={onClose}
            desktopWidth="1220px"
            fullWidthOnMobile={true}
            header={
                <>
                    <div className={styles.header}>
                        <Typography tagVariant={TypographyTagVariants.h5} fontWeight="900" fontSize="20">
                            {translations.title}
                        </Typography>
                    </div>
                    <hr className={styles.divider} />
                </>
            }>
            <div className={styles.content}>
                <div className="d-flex flex-column gap-3">
                    <Typography classes="mb-1" fontSize="16">
                        <>
                            {`${translations.merchantLabel} `}
                            <Typography fontWeight="800" tagVariant={TypographyTagVariants.span}>
                                {merchant}
                            </Typography>
                        </>
                    </Typography>
                    <ProductQuantityCard
                        uuid={product.uuid}
                        name={product.name}
                        image={product.image}
                        price={dataCalculateProductPrice?.price}
                        quantity={product.quantity}
                        attributes={attributesValuesNames}
                        availability={product.availability}
                        setQuantity={(value) => setQuantity(value)}
                    />
                    <div className="col-12">
                        <Input
                            type={InputTypes.textarea}
                            variant={InputVariants.labeledInput}
                            label={`${translations.descriptionLabel}*`}
                            placeholder={translations.descriptionPlaceholder}
                            value={description}
                            disabled={loadingReserveProduct}
                            onChange={(value) => setDescription(value)}
                            state={errors.description.length > 0 ? InputStates.error : undefined}
                            errorMessages={errors.description}
                        />
                        <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                    </div>
                    <Typography>
                        <>{translations.yourDataLabel}:</>
                    </Typography>
                    <Typography>
                        <>
                            {translations.yourDataNameLabel}: {user?.data?.firstName} {user?.data?.lastName}
                        </>
                        <br />
                        <>
                            {translations.yourDataEmailLabel}: {user?.data?.email}
                        </>
                        <br />
                        <>
                            {translations.yourDataPhoneLabel}: {user?.data?.phone && formatPhoneNumber(user?.data?.phone)}
                        </>
                    </Typography>
                    <Typography>{translations.waitForStoreFeedbackLabel}</Typography>
                    <>
                        {errors.general && (
                            <>
                                <ErrorMessages messages={errors.general} />
                                <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                            </>
                        )}
                    </>
                    <div className="d-flex gap-3 align-self-end">
                        <Button label={translations.cancelButton} mode={ButtonMode.danger} onClick={onClose} />
                        <Button
                            label={translations.reserveButton}
                            mode={ButtonMode.primary}
                            onClick={handleOnClickReserve}
                            disabled={loadingReserveProduct || loadingCalculateProductPrice}
                        />
                    </div>
                </div>
            </div>
        </Popup>
    );
};
